import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FloatingPortal } from '@floating-ui/react';
import { Label, Listbox, ListboxOptions, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';

import { cn } from 'support/helpers/cn/cn';
import { WithRef } from 'support/types/withRef';

import { deserializeValue, getControlledValue, getSelectedOptions, serializeValue } from './utils';
import { ComboboxDropdown } from './ComboboxDropdown';
import { DropdownButton } from './components/DropdownButton';
import { DropdownOptions } from './components/DropdownOptions';
import { ListboxHandlingOpenState } from './components/ListboxHandlingOpenState';
import { dropdownClasses } from './styles';
import { DropdownType } from './types';
import { useDropdownFloating } from './useDropdownFloating';

export const Dropdown = <TValue,>({ searchEnabled, ...props }: WithRef<DropdownType<TValue>, HTMLInputElement>) => {
  if (searchEnabled) {
    return <ComboboxDropdown {...props} />;
  }

  return <ListboxDropdown {...props} />;
};

const ListboxDropdown = <TValue,>({
  label,
  description,
  required,
  emptySelectionLabel,
  options = [],
  name,
  value,
  onChange,
  onFocus,
  onBlur,
  onOpen,
  onClose,
  errors,
  fieldState,
  disabled,
  className,
  buttonClassName,
  wrapperClassName,
  id,
  showSelection = true,
  leftIcon,
  rightIcon,
  variant = 'default',
  size = 'base',
  dropdownDisplayTestId,
  multiple,
  ref,
}: WithRef<DropdownType<TValue>, HTMLInputElement>) => {
  const { t } = useTranslation();
  const resolvedFieldState = fieldState ?? (errors ? 'error' : 'default');
  const {
    wrapperRef,
    checkIconRef,
    checkIconColumnWidth,
    floatingRefs,
    floatingStyles,
    floatingWidth,
    getReferenceProps,
    getFloatingProps,
    createBlurHandler,
  } = useDropdownFloating();

  const handleOnBlur = createBlurHandler(onBlur);

  const isMultipleSelection = Boolean(multiple);

  const selectedOptions = getSelectedOptions(options, value, isMultipleSelection);
  const controlledValue = getControlledValue(value, isMultipleSelection);
  const isLarge = variant === 'large';

  return (
    <div ref={wrapperRef} className={wrapperClassName} id={id}>
      <input
        ref={ref}
        type="text"
        value={serializeValue(controlledValue, isMultipleSelection)}
        name={name}
        aria-label={name}
        className="hidden"
        onChange={(e) => {
          const deserializedValue = deserializeValue<TValue>(e.target.value, isMultipleSelection);
          onChange?.(deserializedValue as any);
        }}
      />

      <Listbox
        // `??`, not `||`: a real `false` selection (e.g. a Yes/No field's "No") must stay `false`,
        // not collapse to `''` and read as nothing selected — the selected option's checkmark and
        // bold styling depend on this value, even though the button label and hidden mirror input
        // are unaffected either way (they compare with `===`, not through this prop).
        value={controlledValue ?? ''}
        onChange={onChange as any}
        disabled={disabled}
        multiple={isMultipleSelection}
      >
        {({ open }) => {
          return (
            <ListboxHandlingOpenState open={open} onOpen={onOpen} onClose={onClose}>
              <div className={className}>
                {(label || description) && isLarge ? (
                  <div className="mb-2 flex flex-col gap-1">
                    {label ? (
                      <Label className="block text-base font-semibold text-wrap text-gray-900">
                        {t(label)}
                        {required && <span className="text-procuros-green-600"> *</span>}
                      </Label>
                    ) : null}
                    {description && <p className="text-sm text-gray-700">{description}</p>}
                  </div>
                ) : label ? (
                  <Label className="mb-1 block text-sm text-wrap text-gray-500">
                    {t(label)}
                    {required && <span className="text-procuros-green-600">*</span>}
                  </Label>
                ) : null}

                <div className="relative flex">
                  <DropdownButton
                    selectedOptions={selectedOptions}
                    emptySelectionLabel={emptySelectionLabel}
                    showSelection={showSelection}
                    size={size}
                    buttonClassName={buttonClassName}
                    fieldState={resolvedFieldState}
                    open={open}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                    disabled={disabled}
                    name={name}
                    referenceProps={getReferenceProps({
                      ref: floatingRefs.setReference,
                    })}
                    onBlur={handleOnBlur}
                    onFocus={onFocus}
                    displayTestId={dropdownDisplayTestId}
                  />

                  {/* Hidden measurer: mirrors the selected option's check icon so the
                      panel width can reserve its column. Kept out of flow and hidden. */}
                  <span
                    ref={checkIconRef}
                    aria-hidden="true"
                    className="pointer-events-none invisible absolute flex items-center"
                  >
                    <CheckIcon className="size-4" />
                  </span>

                  <FloatingPortal>
                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions
                        as="ul"
                        className={cn(
                          dropdownClasses.list.size[size],
                          'z-50 max-h-60 w-full overflow-auto bg-white shadow-lg ring-1 ring-black/5 focus-visible:outline-hidden',
                        )}
                        ref={floatingRefs.setFloating}
                        style={{ ...floatingStyles, width: `${floatingWidth + checkIconColumnWidth}px` }}
                        {...getFloatingProps()}
                      >
                        <DropdownOptions options={options} size={size} />
                      </ListboxOptions>
                    </Transition>
                  </FloatingPortal>
                </div>

                {Boolean(errors) && <p className="mt-2 text-sm text-red-600">{errors?.message}</p>}
                {Boolean(description) && !isLarge && <p className="mt-2 text-sm text-gray-500">{description}</p>}
              </div>
            </ListboxHandlingOpenState>
          );
        }}
      </Listbox>
    </div>
  );
};
